import React, { useState, useMemo } from 'react';
import { RaceLap, TelemetryLapData } from '../../models/TelemetryData';
import { getLapColor, REF_LAP_ID } from './LapSelector';
import { formatMilliseconds } from '../../utils/time';

interface RefLapEntry {
  id: string;
  lap: number;
  driverName: string;
  time: number;
  sec1: number;
  sec2: number;
  sec3: number;
}

interface StatStripProps {
  laps: RaceLap[];
  checkedLapIds: string[];
  lapDataMap: Map<string, TelemetryLapData>;
  baselineLapId: string;
  allLapIds: string[];
  refLap?: RefLapEntry | null;
  collapsed?: boolean;
}

function stdDev(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map((v) => (v - mean) ** 2);
  return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length);
}

const StatStrip: React.FC<StatStripProps> = ({
  laps,
  checkedLapIds,
  lapDataMap,
  baselineLapId,
  allLapIds,
  refLap,
  collapsed,
}) => {
  const [mode, setMode] = useState<'comparison' | 'consistency'>('comparison');
  const [isExpanded, setIsExpanded] = useState(true);
  const [barExpanded, setBarExpanded] = useState(false);

  // ── Comparison mode computations ──────────────────────────────────────────

  const checkedLaps = useMemo(
    () => laps.filter((l) => checkedLapIds.includes(l.id)),
    [laps, checkedLapIds]
  );

  // All valid session laps — the pool for Comparison bests and Optimal.
  // Excludes the reference lap and any invalid/zero-time laps.
  const validLaps = useMemo(
    () => laps.filter((l) => l.valid && l.time > 0),
    [laps]
  );

  // Sector bests across valid session laps (ignoring 0 values). Reference excluded.
  const sectorBests = useMemo(() => {
    const validSec = (arr: number[]) => arr.filter((v) => v > 0);
    const sec1s = validSec(validLaps.map((l) => l.sec1));
    const sec2s = validSec(validLaps.map((l) => l.sec2));
    const sec3s = validSec(validLaps.map((l) => l.sec3));
    return {
      s1: sec1s.length ? Math.min(...sec1s) : 0,
      s2: sec2s.length ? Math.min(...sec2s) : 0,
      s3: sec3s.length ? Math.min(...sec3s) : 0,
      hasSector3: sec3s.length > 0,
    };
  }, [validLaps]);

  // Fastest total lap time across valid session laps. Reference excluded.
  const bestLapTime = useMemo(() => {
    const times = validLaps.map((l) => l.time).filter((t) => t > 0);
    return times.length ? Math.min(...times) : 0;
  }, [validLaps]);

  // Optimal (theoretical best) lap = sum of the best sector times available.
  // Only meaningful once every contributing sector has a valid best.
  const optimalLapTime = useMemo(() => {
    const { s1, s2, s3, hasSector3 } = sectorBests;
    if (s1 <= 0 || s2 <= 0 || (hasSector3 && s3 <= 0)) return 0;
    return s1 + s2 + (hasSector3 ? s3 : 0);
  }, [sectorBests]);

  // Top / min speed from telemetry frames (session laps + ref lap)
  const speedStats = useMemo(() => {
    const result: { lapId: string; label: string; topSpeed: number; minSpeed: number }[] = [];

    if (refLap) {
      const data = lapDataMap.get(REF_LAP_ID);
      if (data) {
        const speeds = Object.values(data.t).map((f) => f.v);
        if (speeds.length > 0) {
          result.push({ lapId: REF_LAP_ID, label: 'REF', topSpeed: Math.max(...speeds), minSpeed: Math.min(...speeds) });
        }
      }
    }

    for (const lapId of checkedLapIds) {
      const data = lapDataMap.get(lapId);
      if (!data) continue;
      const speeds = Object.values(data.t).map((f) => f.v);
      if (speeds.length === 0) continue;
      const lap = laps.find((l) => l.id === lapId);
      result.push({ lapId, label: `L${lap?.lap ?? '?'}`, topSpeed: Math.max(...speeds), minSpeed: Math.min(...speeds) });
    }
    return result;
  }, [checkedLapIds, lapDataMap, refLap, laps]);

  // Note: formatSec is a hoisted function declaration defined later in this file.
  // If refactoring formatSec to a const arrow function, move it above this memo.
  const slimBarText = useMemo(() => {
    const firstLap = laps.find((l) => l.id === baselineLapId) ?? checkedLaps[0];
    const maxTopSpeed = speedStats.length > 0
      ? Math.max(...speedStats.map((s) => s.topSpeed))
      : 0;
    const s1 = firstLap ? formatSec(firstLap.sec1) : '—';
    const s2 = firstLap ? formatSec(firstLap.sec2) : '—';
    const topMph = maxTopSpeed > 0 ? `${Math.round(maxTopSpeed * 0.621371)} mph` : '—';
    if (sectorBests.hasSector3 && firstLap && firstLap.sec3 > 0) {
      return `S1 ${s1} · S2 ${s2} · S3 ${formatSec(firstLap.sec3)} · Top ${topMph}`;
    }
    return `S1 ${s1} · S2 ${s2} · Top ${topMph}`;
  }, [checkedLaps, speedStats, sectorBests, baselineLapId, laps]);

  // Biggest gap sector analysis
  const biggestGapSector = useMemo(() => {
    const sectors = ['s1', 's2', 's3'] as const;
    type SectorKey = (typeof sectors)[number];
    const lapField: Record<SectorKey, keyof RaceLap> = {
      s1: 'sec1',
      s2: 'sec2',
      s3: 'sec3',
    };

    let maxGap = -1;
    let focusSector: string = 'S1';
    let focusDelta = 0;

    for (const sec of sectors) {
      const values = validLaps
        .map((l) => l[lapField[sec]] as number)
        .filter((v) => v > 0);
      if (values.length < 2) continue;
      const gap = Math.max(...values) - Math.min(...values);
      if (gap > maxGap) {
        maxGap = gap;
        focusSector = sec.toUpperCase();
        focusDelta = gap;
      }
    }

    return { sector: focusSector, delta: focusDelta };
  }, [validLaps]);

  // ── Consistency mode computations ─────────────────────────────────────────

  const consistencyStats = useMemo(() => {
    const validLaps = laps.filter((l) => l.valid && l.time > 0);

    const lapTimes = validLaps.map((l) => l.time);
    const lapTimeSigmaMs = stdDev(lapTimes);
    const lapTimeSigmaSec = lapTimeSigmaMs / 1000;

    const sec1s = validLaps.map((l) => l.sec1).filter((v) => v > 0);
    const sec2s = validLaps.map((l) => l.sec2).filter((v) => v > 0);
    const sec3s = validLaps.map((l) => l.sec3).filter((v) => v > 0);

    const s1Sigma = stdDev(sec1s) / 1000;
    const s2Sigma = stdDev(sec2s) / 1000;
    const s3Sigma = sec3s.length > 0 ? stdDev(sec3s) / 1000 : null;

    const bestTime = lapTimes.length ? Math.min(...lapTimes) : 0;
    const worstTime = lapTimes.length ? Math.max(...lapTimes) : 0;
    const spread = worstTime - bestTime;

    const validPct =
      laps.length > 0
        ? Math.round((validLaps.length / laps.length) * 100)
        : 0;

    // Weakest sector: highest sigma
    const sectorSigmas: { label: string; sigma: number }[] = [
      { label: 'S1', sigma: s1Sigma },
      { label: 'S2', sigma: s2Sigma },
    ];
    if (s3Sigma !== null) {
      sectorSigmas.push({ label: 'S3', sigma: s3Sigma });
    }
    const weakest = sectorSigmas.reduce(
      (prev, cur) => (cur.sigma > prev.sigma ? cur : prev),
      sectorSigmas[0] ?? { label: 'S1', sigma: 0 }
    );

    return {
      lapTimeSigmaSec,
      s1Sigma,
      s2Sigma,
      s3Sigma,
      bestTime,
      worstTime,
      spread,
      validPct,
      validCount: validLaps.length,
      totalCount: laps.length,
      weakestSector: weakest.label,
      weakestSigma: weakest.sigma,
    };
  }, [laps]);

  // ── Render helpers ────────────────────────────────────────────────────────

  function formatSec(ms: number): string {
    if (ms === 0) return '—';
    return (ms / 1000).toFixed(3);
  }

  // Compact lap-time format: "52.561" under a minute, "1:08.214" at/over a minute.
  function formatLapTime(ms: number): string {
    if (ms <= 0) return '—';
    const totalSec = ms / 1000;
    if (totalSec < 60) return totalSec.toFixed(3);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec - minutes * 60;
    return `${minutes}:${seconds.toFixed(3).padStart(6, '0')}`;
  }

  function deltaStr(ms: number, bestMs: number): string {
    if (ms === 0 || bestMs === 0) return 'n/a';
    const diff = ms - bestMs;
    if (diff === 0) return 'best';
    return `+${(diff / 1000).toFixed(3)}`;
  }

  function deltaColor(ms: number, bestMs: number): string {
    if (ms === 0 || bestMs === 0) return '#999';
    return ms === bestMs ? '#2ecc71' : '#e74c3c';
  }

  function sigmaColor(sigma: number): string {
    if (sigma < 0.5) return '#2ecc71';
    if (sigma <= 1.0) return '#e5e2e1';
    return '#e74c3c';
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {collapsed && (
        <div className="stat-strip-bar">
          <span className="stat-strip-bar-content">{slimBarText}</span>
          <span
            className={`stat-strip-bar-chevron${barExpanded ? ' open' : ''}`}
            onClick={() => setBarExpanded((p) => !p)}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ display: 'block' }}>
              <polyline points={barExpanded ? '2,8 6,4 10,8' : '2,4 6,8 10,4'} />
            </svg>
          </span>
        </div>
      )}
      {(!collapsed || barExpanded) && <div className={`stat-strip ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header */}
      <div className="stat-strip-header">
        <span className="stat-strip-mode-label">STATS</span>

        <div className="stat-strip-toggle">
          <button
            className={mode === 'comparison' ? 'active' : ''}
            onClick={() => { setMode('comparison'); setIsExpanded(true); }}
          >
            Comparison
          </button>
          <button
            className={mode === 'consistency' ? 'active' : ''}
            onClick={() => { setMode('consistency'); setIsExpanded(true); }}
          >
            Consistency
          </button>
        </div>

        <button
          className={`stat-strip-collapse ${isExpanded ? 'up' : ''}`}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,4 6,8 10,4" /></svg>
        </button>
      </div>

      {/* Cards */}
      <div className="stat-strip-cards">
          {mode === 'comparison' ? (
            <>
              {/* Lap Time */}
              <div className="stat-card">
                <div className="stat-card-label">Lap Time</div>
                <div className="stat-card-values">
                  {refLap && refLap.time > 0 && (
                    <div className="stat-card-row">
                      <span className="stat-card-dot" style={{ background: '#d4a843' }} />
                      <span className="stat-card-lap" style={{ color: '#d4a843' }}>REF</span>
                      <span className="stat-card-value" style={{ color: deltaColor(refLap.time, bestLapTime) }}>{formatLapTime(refLap.time)}</span>
                      <span className="stat-card-delta" style={{ color: deltaColor(refLap.time, bestLapTime) }}>{deltaStr(refLap.time, bestLapTime)}</span>
                    </div>
                  )}
                  {checkedLapIds.map((lapId) => {
                    const lap = laps.find((l) => l.id === lapId);
                    if (!lap) return null;
                    const color = getLapColor(lapId, allLapIds);
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap">L{lap.lap}</span>
                        <span className="stat-card-value" style={{ color: deltaColor(lap.time, bestLapTime) }}>{formatLapTime(lap.time)}</span>
                        <span className="stat-card-delta" style={{ color: deltaColor(lap.time, bestLapTime) }}>{deltaStr(lap.time, bestLapTime)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sector 1 */}
              <div className="stat-card">
                <div className="stat-card-label">Sector 1</div>
                <div className="stat-card-values">
                  {refLap && refLap.sec1 > 0 && (
                    <div className="stat-card-row">
                      <span className="stat-card-dot" style={{ background: '#d4a843' }} />
                      <span className="stat-card-lap" style={{ color: '#d4a843' }}>REF</span>
                      <span className="stat-card-value" style={{ color: deltaColor(refLap.sec1, sectorBests.s1) }}>{formatSec(refLap.sec1)}</span>
                      <span className="stat-card-delta" style={{ color: deltaColor(refLap.sec1, sectorBests.s1) }}>{deltaStr(refLap.sec1, sectorBests.s1)}</span>
                    </div>
                  )}
                  {checkedLapIds.map((lapId) => {
                    const lap = laps.find((l) => l.id === lapId);
                    if (!lap) return null;
                    const color = getLapColor(lapId, allLapIds);
                    const val = lap.sec1;
                    const best = sectorBests.s1;
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap">L{lap.lap}</span>
                        <span className="stat-card-value" style={{ color: deltaColor(val, best) }}>{formatSec(val)}</span>
                        <span className="stat-card-delta" style={{ color: deltaColor(val, best) }}>{deltaStr(val, best)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sector 2 */}
              <div className="stat-card">
                <div className="stat-card-label">Sector 2</div>
                <div className="stat-card-values">
                  {refLap && refLap.sec2 > 0 && (
                    <div className="stat-card-row">
                      <span className="stat-card-dot" style={{ background: '#d4a843' }} />
                      <span className="stat-card-lap" style={{ color: '#d4a843' }}>REF</span>
                      <span className="stat-card-value" style={{ color: deltaColor(refLap.sec2, sectorBests.s2) }}>{formatSec(refLap.sec2)}</span>
                      <span className="stat-card-delta" style={{ color: deltaColor(refLap.sec2, sectorBests.s2) }}>{deltaStr(refLap.sec2, sectorBests.s2)}</span>
                    </div>
                  )}
                  {checkedLapIds.map((lapId) => {
                    const lap = laps.find((l) => l.id === lapId);
                    if (!lap) return null;
                    const color = getLapColor(lapId, allLapIds);
                    const val = lap.sec2;
                    const best = sectorBests.s2;
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap">L{lap.lap}</span>
                        <span className="stat-card-value" style={{ color: deltaColor(val, best) }}>{formatSec(val)}</span>
                        <span className="stat-card-delta" style={{ color: deltaColor(val, best) }}>{deltaStr(val, best)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sector 3 — only when data exists */}
              {sectorBests.hasSector3 && <div className="stat-card">
                <div className="stat-card-label">Sector 3</div>
                <div className="stat-card-values">
                  {refLap && refLap.sec3 > 0 && (
                    <div className="stat-card-row">
                      <span className="stat-card-dot" style={{ background: '#d4a843' }} />
                      <span className="stat-card-lap" style={{ color: '#d4a843' }}>REF</span>
                      <span className="stat-card-value" style={{ color: deltaColor(refLap.sec3, sectorBests.s3) }}>{formatSec(refLap.sec3)}</span>
                      <span className="stat-card-delta" style={{ color: deltaColor(refLap.sec3, sectorBests.s3) }}>{deltaStr(refLap.sec3, sectorBests.s3)}</span>
                    </div>
                  )}
                  {checkedLapIds.map((lapId) => {
                    const lap = laps.find((l) => l.id === lapId);
                    if (!lap) return null;
                    const color = getLapColor(lapId, allLapIds);
                    const val = lap.sec3;
                    const best = sectorBests.s3;
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap">L{lap.lap}</span>
                        <span className="stat-card-value" style={{ color: deltaColor(val, best) }}>{formatSec(val)}</span>
                        <span className="stat-card-delta" style={{ color: deltaColor(val, best) }}>{deltaStr(val, best)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>}

              <div className="stat-divider" />

              {/* Top Speed */}
              <div className="stat-card">
                <div className="stat-card-label">Top Speed</div>
                <div className="stat-card-values">
                  {speedStats.map(({ lapId, label, topSpeed }) => {
                    const color = getLapColor(lapId, allLapIds);
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap" style={lapId === REF_LAP_ID ? { color: '#d4a843' } : undefined}>{label}</span>
                        <span className="stat-card-value" style={{ color: '#00daf3' }}>{Math.round(topSpeed * 0.621371)}</span>
                        <span className="stat-card-unit">mph</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Min Speed */}
              <div className="stat-card">
                <div className="stat-card-label">Min Speed</div>
                <div className="stat-card-values">
                  {speedStats.map(({ lapId, label, minSpeed }) => {
                    const color = getLapColor(lapId, allLapIds);
                    return (
                      <div key={lapId} className="stat-card-row">
                        <span className="stat-card-dot" style={{ background: color }} />
                        <span className="stat-card-lap" style={lapId === REF_LAP_ID ? { color: '#d4a843' } : undefined}>{label}</span>
                        <span className="stat-card-value" style={{ color: '#00daf3' }}>{Math.round(minSpeed * 0.621371)}</span>
                        <span className="stat-card-unit">mph</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="stat-divider" />

              {/* Biggest Gap */}
              <div className="stat-card">
                <div className="stat-card-label">Biggest Gap</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#e74c3c' }}>
                      {biggestGapSector.sector}: +{(biggestGapSector.delta / 1000).toFixed(3)}
                    </span>
                  </div>
                  <div className="stat-card-row">
                    <span className="focus-badge">Focus area</span>
                  </div>
                </div>
              </div>

              {/* Optimal Lap — sum of best sectors */}
              <div className="stat-card">
                <div className="stat-card-label">Optimal Lap</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#2ecc71' }}>
                      {formatLapTime(optimalLapTime)}
                    </span>
                  </div>
                  <div className="stat-card-row">
                    {optimalLapTime > 0 && bestLapTime > 0 ? (
                      <span className="stat-card-sub">
                        {bestLapTime > optimalLapTime
                          ? `−${((bestLapTime - optimalLapTime) / 1000).toFixed(3)}s vs best`
                          : 'matches best'}
                      </span>
                    ) : (
                      <span className="stat-card-sub">best of all sectors</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Lap Time σ */}
              <div className="stat-card">
                <div className="stat-card-label">Lap Time σ</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span
                      className="stat-card-value single"
                      style={{ color: sigmaColor(consistencyStats.lapTimeSigmaSec) }}
                    >
                      {consistencyStats.lapTimeSigmaSec.toFixed(3)}s
                    </span>
                  </div>
                </div>
                <div className="stat-card-sub">across {consistencyStats.validCount} valid laps</div>
              </div>

              {/* S1 Consistency */}
              <div className="stat-card">
                <div className="stat-card-label">S1 Consistency</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#e5e2e1' }}>
                      {consistencyStats.s1Sigma.toFixed(3)}s
                    </span>
                  </div>
                </div>
                <div className="stat-card-sub">σ of sector 1 times</div>
              </div>

              {/* S2 Consistency */}
              <div className="stat-card">
                <div className="stat-card-label">S2 Consistency</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#e5e2e1' }}>
                      {consistencyStats.s2Sigma.toFixed(3)}s
                    </span>
                  </div>
                </div>
                <div className="stat-card-sub">σ of sector 2 times</div>
              </div>

              {/* S3 Consistency */}
              <div className="stat-card">
                <div className="stat-card-label">S3 Consistency</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#e5e2e1' }}>
                      {consistencyStats.s3Sigma !== null
                        ? `${consistencyStats.s3Sigma.toFixed(3)}s`
                        : '—'}
                    </span>
                  </div>
                </div>
                <div className="stat-card-sub">
                  {consistencyStats.s3Sigma !== null ? 'σ of sector 3 times' : 'sec3 = 0 in API'}
                </div>
              </div>

              <div className="stat-divider" />

              {/* Best / Worst */}
              <div className="stat-card">
                <div className="stat-card-label">Best / Worst</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value" style={{ color: '#2ecc71', fontSize: '14px' }}>
                      {consistencyStats.bestTime > 0
                        ? formatMilliseconds(consistencyStats.bestTime)
                        : '—'}
                    </span>
                    <span style={{ color: '#444', fontSize: '12px' }}>/</span>
                    <span className="stat-card-value" style={{ color: '#e74c3c', fontSize: '14px' }}>
                      {consistencyStats.worstTime > 0
                        ? formatMilliseconds(consistencyStats.worstTime)
                        : '—'}
                    </span>
                  </div>
                </div>
                <div className="stat-card-sub">
                  spread: {(consistencyStats.spread / 1000).toFixed(3)}s
                </div>
              </div>

              {/* Valid Laps % */}
              <div className="stat-card">
                <div className="stat-card-label">Valid Laps</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#2ecc71' }}>
                      {consistencyStats.validPct}%
                    </span>
                  </div>
                  <div className="stat-card-row">
                    <span className="stat-card-sub">
                      {consistencyStats.validCount} of {consistencyStats.totalCount} laps
                    </span>
                  </div>
                </div>
              </div>

              {/* Weakest Sector */}
              <div className="stat-card">
                <div className="stat-card-label">Weakest Sector</div>
                <div className="stat-card-values">
                  <div className="stat-card-row">
                    <span className="stat-card-value single" style={{ color: '#e74c3c' }}>
                      {consistencyStats.weakestSector}
                    </span>
                  </div>
                  <div className="stat-card-row">
                    <span className="focus-badge">highest variance</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>}
    </>
  );
};

export default StatStrip;
